import { useCallback, useRef } from 'react';

import { createClient, type ListenLiveClient, LiveTranscriptionEvents } from '@deepgram/sdk';
import type { LocalAudioTrack, RemoteAudioTrack } from 'twilio-video';

interface TranscriptData {
  text: string;
  speaker: 'Tutor' | 'Student';
}

interface ConnectionData {
  connection: ListenLiveClient;
  mediaRecorder: MediaRecorder;
}

export function useDeepgram() {
  const connectionsRef = useRef<Map<string, ConnectionData>>(new Map());
  const connectingRef = useRef<Set<string>>(new Set());

  const startTranscription = useCallback((
    participantId: string,
    audioTrack: RemoteAudioTrack | LocalAudioTrack,
    speaker: 'Tutor' | 'Student',
    onTranscript: (data: TranscriptData) => void,
    finalOnly: boolean = true
  ) => {
    const apiKey = import.meta.env.VITE_DEEPGRAM_API_KEY;
    if (!apiKey) {
      console.error('Deepgram: missing API key (set VITE_DEEPGRAM_API_KEY)');
      return;
    }

    // Prevent duplicate connection attempts
    if (connectingRef.current.has(participantId)) {
      return;
    }

    // Stop existing transcription for this participant if any
    if (connectionsRef.current.has(participantId)) {
      const existing = connectionsRef.current.get(participantId);
      if (existing) {
        try {
          existing.mediaRecorder.stop();
        } catch {
          // MediaRecorder might already be stopped
        }
        try {
          existing.connection.requestClose?.();
        } catch {
          // Connection might already be closed
        }
      }
      connectionsRef.current.delete(participantId);
    }

    // Validate audio track
    if (!audioTrack || !audioTrack.mediaStreamTrack) {
      console.error(`Deepgram: Invalid audio track provided for participant ${participantId}`);
      return;
    }

    connectingRef.current.add(participantId);

    let deepgram;
    try {
      deepgram = createClient(apiKey);
    } catch (error) {
      connectingRef.current.delete(participantId);
      console.error(`Deepgram: Failed to create client for participant ${participantId}:`, error);
      return;
    }

    let conn: ListenLiveClient;
    try {
      conn = deepgram.listen.live({
        model: 'nova-3',
        language: 'multi',
        smart_format: true,
        endpointing: 100,
      });
    } catch (error) {
      connectingRef.current.delete(participantId);
      console.error(`Deepgram: Failed to create connection for participant ${participantId}:`, error);
      return;
    }

    let mediaRecorder: MediaRecorder | null = null;
    let connectionOpened = false;
    let timeoutId: NodeJS.Timeout | null = null;
    let keepAliveInterval: NodeJS.Timeout | null = null;

    timeoutId = setTimeout(() => {
      if (!connectionOpened && !connectionsRef.current.has(participantId)) {
        // Connection never opened, clean up
        connectingRef.current.delete(participantId);
        console.warn(`Deepgram: Connection timeout for participant ${participantId}`);
        try {
          conn.requestClose?.();
        } catch {
          // Ignore errors
        }
      }
    }, 10000);

    // Set up MediaRecorder BEFORE connection opens to ensure we can send data immediately
    let stream: MediaStream;
    try {
      stream = new MediaStream([audioTrack.mediaStreamTrack]);

      let mimeType = 'audio/webm; codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = '';
        }
      }

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorder = recorder;

      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) {
          try {
            const readyState = (conn as any).getReadyState?.();
            if (readyState === 1 || readyState === undefined) {
              conn.send(e.data);
            }
          } catch (error) {
            console.error('Deepgram: Error sending audio data:', error);
          }
        }
      };

      recorder.onerror = (event) => {
        console.error('Deepgram: MediaRecorder error:', event);
      };
    } catch (error) {
      connectingRef.current.delete(participantId);
      console.error('Deepgram: Error setting up MediaRecorder:', error);
      return;
    }

    conn.on(LiveTranscriptionEvents.Open, () => {
      connectionOpened = true;
      connectingRef.current.delete(participantId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (stateCheckInterval) {
        clearInterval(stateCheckInterval);
        stateCheckInterval = null;
      }

      try {
        // Start MediaRecorder immediately when connection opens
        if (mediaRecorder && mediaRecorder.state === 'inactive') {
          mediaRecorder.start(250);
        }

        // Set up KeepAlive to send every 3 seconds to prevent timeout during silence
        keepAliveInterval = setInterval(() => {
          try {
            const readyState = (conn as any).getReadyState?.();
            if (readyState === 1) {
              conn.send(JSON.stringify({ type: 'KeepAlive' }));
            }
          } catch (error) {
            console.error('Deepgram: Error sending KeepAlive:', error);
          }
        }, 3000);

        connectionsRef.current.set(participantId, {
          connection: conn,
          mediaRecorder: mediaRecorder,
        });
      } catch (error) {
        console.error('Deepgram: Error starting MediaRecorder:', error);
        if (keepAliveInterval) {
          clearInterval(keepAliveInterval);
        }
        conn.requestClose?.();
      }
    });

    conn.on(LiveTranscriptionEvents.Transcript, (data) => {
      // Process transcripts based on finalOnly flag
      if (finalOnly && !data.is_final) {
        return;
      }

      const transcript = data.channel.alternatives[0]?.transcript;
      if (transcript && transcript.trim().length > 0) {
        onTranscript({
          text: transcript,
          speaker,
        });
      }
    });

    conn.on(LiveTranscriptionEvents.Error, (err: any) => {
      connectingRef.current.delete(participantId);
      if (keepAliveInterval) {
        clearInterval(keepAliveInterval);
        keepAliveInterval = null;
      }

      console.error('Deepgram: Error details:', {
        message: err.message,
        statusCode: err.statusCode,
        readyState: err.readyState,
        url: err.url,
        hasOriginalEvent: !!err.originalEvent,
      });

      if (mediaRecorder) {
        try {
          if (mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
          }
        } catch {
          // Ignore errors when stopping
        }
      }
      connectionsRef.current.delete(participantId);
    });

    conn.on(LiveTranscriptionEvents.Close, () => {
      connectingRef.current.delete(participantId);
      if (keepAliveInterval) {
        clearInterval(keepAliveInterval);
        keepAliveInterval = null;
      }
      if (stateCheckInterval) {
        clearInterval(stateCheckInterval);
        stateCheckInterval = null;
      }
      if (mediaRecorder) {
        try {
          if (mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
          }
        } catch {
          // Ignore errors when stopping
        }
      }
      connectionsRef.current.delete(participantId);
    });

    let stateCheckInterval: NodeJS.Timeout | null = null;
    try {
      stateCheckInterval = setInterval(() => {
        try {
          const readyState = (conn as any).getReadyState?.();
          if (readyState !== undefined) {
            // Stop checking once connection is open or closed
            if (readyState === 1 || readyState === 3) {
              if (stateCheckInterval) {
                clearInterval(stateCheckInterval);
                stateCheckInterval = null;
              }
            }
          }
        } catch {
          // Ignore if getReadyState is not available
        }
      }, 500);
    } catch (error) {
      console.error('Deepgram: Error checking connection state:', error);
    }
  }, []);

  const stopTranscription = useCallback((participantId?: string) => {
    if (participantId) {
      const connectionData = connectionsRef.current.get(participantId);
      if (connectionData) {
        try {
          connectionData.mediaRecorder.stop();
        } catch {
          // MediaRecorder might already be stopped
        }
        try {
          connectionData.connection.requestClose?.();
        } catch {
          // Connection might already be closed
        }
        connectionsRef.current.delete(participantId);
      }
    } else {
      // Stop all transcriptions
      connectionsRef.current.forEach((connectionData) => {
        try {
          connectionData.mediaRecorder.stop();
        } catch {
          // MediaRecorder might already be stopped
        }
        try {
          connectionData.connection.requestClose?.();
        } catch {
          // Connection might already be closed
        }
      });
      connectionsRef.current.clear();
    }
  }, []);

  return {
    startTranscription,
    stopTranscription,
  };
}